import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone, KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function TwoFactorVerify() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const challengeToken =
        typeof window !== "undefined"
            ? sessionStorage.getItem("twofa_challenge_token")
            : null;
    const email =
        typeof window !== "undefined" ? sessionStorage.getItem("twofa_email") : null;

    const [mode, setMode] = useState("totp"); // totp | backup
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!challengeToken) navigate("/login");
    }, [challengeToken, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!code || submitting) return;
        const trimmed = code.trim();
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/2fa/verify", {
                challenge_token: challengeToken,
                code: trimmed,
            });
            sessionStorage.removeItem("twofa_challenge_token");
            sessionStorage.removeItem("twofa_email");
            localStorage.setItem("token", data.access_token);
            await refresh();
            toast.success(`Welcome back, ${data.user.name}`);
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "That code didn't work. Please try again."
            );
            setCode("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center px-4 py-12"
            data-testid="twofa-verify-page"
        >
            <Card className="w-full max-w-md border-border shadow-lg shadow-primary/5">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center ring-1 ring-primary/15">
                            <Smartphone className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-foreground tracking-tight leading-none">
                                Two-step verification
                            </h1>
                            <p className="text-xs text-muted-foreground mt-1">
                                Signed in as {email}
                            </p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label htmlFor="code" className="text-sm font-semibold">
                                {mode === "totp"
                                    ? "Enter the 6-digit code from your authenticator app"
                                    : "Enter one of your backup codes"}
                            </Label>
                            <Input
                                id="code"
                                value={code}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (mode === "totp") {
                                        setCode(v.replace(/\D/g, "").slice(0, 6));
                                    } else {
                                        setCode(v.toUpperCase().slice(0, 12));
                                    }
                                }}
                                inputMode={mode === "totp" ? "numeric" : "text"}
                                placeholder={mode === "totp" ? "123456" : "ABCD-EFGH"}
                                autoFocus
                                className={
                                    mode === "totp"
                                        ? "font-mono text-center text-lg tracking-[0.4em] mt-1"
                                        : "font-mono text-center mt-1"
                                }
                                data-testid="twofa-verify-code-input"
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full gap-1.5"
                            disabled={
                                submitting ||
                                (mode === "totp" ? !/^\d{6}$/.test(code) : code.length < 6)
                            }
                            data-testid="twofa-verify-submit"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Smartphone className="h-4 w-4" />
                            )}
                            Verify & sign in
                        </Button>

                        <div className="flex items-center justify-between text-xs">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode(mode === "totp" ? "backup" : "totp");
                                    setCode("");
                                }}
                                className="text-primary inline-flex items-center gap-1 hover:underline"
                                data-testid="twofa-verify-toggle-mode"
                            >
                                <KeyRound className="h-3.5 w-3.5" />
                                {mode === "totp"
                                    ? "Use a backup code instead"
                                    : "Use authenticator app instead"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    sessionStorage.removeItem("twofa_challenge_token");
                                    sessionStorage.removeItem("twofa_email");
                                    navigate("/login");
                                }}
                                className="text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" /> Start over
                            </button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
